import { Direction, Movements, toPtzFromDirection, Zero } from '@studio/models/ptz';
import { useDocumentActive, useLatestFn } from '@studio/react-utils/hooks';
import type { MouseEvent, ReactElement } from 'react';
import { useEffect } from 'react';
import type { ChoreographerProps } from './types.js';
import { getTargetIntent } from './utils.js';
import { useRatePref } from '#~/control/movement/hooks.js';
import { Facet } from '#~/control/movement/types.js';

/** `<Choreographer>` component. */
export const Choreographer = ({
  canPan,
  canTilt,
  canZoom,
  children,
  onMove,
  deviceId,
  ...props
}: ChoreographerProps): ReactElement => {
  const isDocumentActive = useDocumentActive();

  const [zRate] = useRatePref(deviceId, Facet.Zoom);
  const [ptRate] = useRatePref(deviceId, Facet.PanTilt);

  // Camera movement handler
  const handleMove = useLatestFn((event: MouseEvent) => {
    const [direction, movement] = getTargetIntent(event);

    if (
      (direction === Direction.North ||
        direction === Direction.Northeast ||
        direction === Direction.Northwest ||
        direction === Direction.South ||
        direction === Direction.Southeast ||
        direction === Direction.Southwest) &&
      !canTilt
    ) {
      return;
    }

    if (
      (direction === Direction.East ||
        direction === Direction.West ||
        direction === Direction.Northeast ||
        direction === Direction.Southeast ||
        direction === Direction.Northwest ||
        direction === Direction.Southwest) &&
      !canPan
    ) {
      return;
    }

    if (!direction && !movement) {
      return;
    }

    const intent = direction ? toPtzFromDirection(direction) : Movements[movement!].ptz;

    let { pan, tilt, zoom } = intent;

    pan = pan * ptRate.value;
    tilt = tilt * ptRate.value;
    zoom = zoom * zRate.value;

    const nextIntent = { pan, tilt, zoom };

    onMove?.(nextIntent);
  });

  // Stop movement handler
  const handleStop = useLatestFn(() => {
    onMove?.(Zero);
  });

  // Stop all movement when the document loses focus.
  useEffect(() => {
    if (!isDocumentActive) {
      handleStop();
    }
  }, [isDocumentActive]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div onMouseDown={handleMove} onMouseLeave={handleStop} onMouseUp={handleStop} {...props}>
      {children}
    </div>
  );
};
